import React, { useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advanced from 'dayjs/plugin/advancedFormat';
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advanced);

import { CatalogContext } from 'src/pages/CatalogPage/CatalogPage';
import {
  CatalogWishlists,
} from 'src/pages/CatalogPage/components/AuctionItems/AuctionItem/AuctionItemActions/CatalogWishlists/CatalogWishlists';
import { PlaceBid } from 'src/components/PlaceBid/PlaceBid';
import {
  CatalogTextAlerts,
} from 'src/pages/CatalogPage/components/AuctionItems/AuctionItem/AuctionItemActions/CatalogTextAlerts/CatalogTextAlerts';
import {
  Actions,
  BidButtons,
  BidSubmittedButton,
  TimedAuctionItemInfo,
  WishlistAndText,
} from 'src/pages/CatalogPage/components/AuctionItems/AuctionItem/AuctionItemActions/AuctionItemActions.style';
import { formatCurrency } from 'src/lib/catalog';
import { IAuctionItem } from 'src/pages/CatalogPage/CatalogPage.i';
import { LoginToUse } from 'src/components/LoginToUse/LoginToUse';
import { BiddingContext } from 'src/lib/BiddingContext';
import { getEndMessage } from 'src/components/AuctionInfo/TimedAuctionInfo';
import { countIncrements } from '@riac/core';

interface IAuctionItemActions {
  item: IAuctionItem;
  compact: boolean;
}

export const getBidCountMessage = (start, end) => {
  const increments = countIncrements(start, end);
  let label = 'bid';
  if(increments === 0) {
    return '';
  }
  if(increments > 1) {
    label += 's';
  }
  return `(${increments} ${label})`;
};

export const AuctionItemActions = (props: IAuctionItemActions): JSX.Element => {
  const endDate = dayjs.tz(props.item.EndDate, 'America/Chicago');
  const now = dayjs().tz('America/Chicago');
  const { auctionTypeId, auctionTitle } = useContext(CatalogContext);
  const [bidInCart, setBidInCart] = useState(false);
  const [timedAuctionLotActive, setTimedAuctionLotActive] = useState(endDate.isAfter(now));
  const { getMessageForLot } = useContext(BiddingContext);

  useEffect(() => {
    const e = dayjs.tz(props.item.EndDate, 'America/Chicago');
    const n = dayjs().tz('America/Chicago');
    const diff = e.diff(n);
    let timer = null;
    if(e.isAfter(n) && diff > 0 && diff < 86400000) {
      setTimedAuctionLotActive(true);
      timer = setTimeout(() => {
        setTimedAuctionLotActive(false);
      }, diff+1000);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [props.item.EndDate]);

  return (
    <Actions compact={props.compact}>
      <LoginToUse
        overlayText="Log In to Place Bids or Create Wishlists and Text Alerts"
      >
        <BidButtons>
          {getMessageForLot(props.item.LotNumber)}
          {timedAuctionLotActive || auctionTypeId !== TIMED_AUCTION_TYPE_ID ? (
            <div>
              <PlaceBid
                type="sealed"
                page="catalog"
                auctionTypeID={auctionTypeId}
                item={props.item}
                addBid={() =>{
                  if(auctionTypeId !== TIMED_AUCTION_TYPE_ID) {
                    setBidInCart(true);
                  }
                }}
              />
              {(auctionTypeId === TIMED_AUCTION_TYPE_ID) && (
                <PlaceBid
                  type={'increment'}
                  page={'catalog'}
                  item={props.item}
                  auctionTypeID={auctionTypeId}
                  addBid={() => {
                  }}
                />
              )}
              {(auctionTypeId !== TIMED_AUCTION_TYPE_ID) && (
                <PlaceBid
                  type="phone"
                  page="catalog"
                  auctionTypeID={auctionTypeId}
                  item={props.item}
                  addBid={() => setBidInCart(true)}
                />
              ) || ''}
            </div>
          ) : ''}
          {auctionTypeId === TIMED_AUCTION_TYPE_ID
            && <TimedAuctionItemInfo className={'space-y-1'}>
              { props.item.CurrentBid && <>
              Current Bid: {props.item.CurrentBid ? formatCurrency(props.item.CurrentBid) : 'No bids'} {getBidCountMessage(props.item.StartingBid, props.item.CurrentBid)}
              </>
                || <>
                Starting Bid: {formatCurrency(props.item.StartingBid)}</>
              }
              <br/>
              <>
                {getEndMessage(now, endDate)}
                <br/>
                {(props.item.HighBidder && (
                  <>
                    <span>Max Bid: {formatCurrency(props.item.HighBidAmount)}</span><br/>
                    <span className={'bg-green-700 text-white p-1 rounded-md'}>
                      You are the high bidder
                    </span>
                  </>
                )) || (props.item.PreviousBid
                    && <span className={'bg-red-700 text-white p-1 rounded-md'}>
                      You are out bid
                    </span>
                    || '')}
              </>
              <br/>
            </TimedAuctionItemInfo>
          }
          {(props.item.citem === 1 || bidInCart) && (
            <>
              <BidSubmittedButton to="/account/cart" title="View Your Bid Cart">
                In Cart
              </BidSubmittedButton>
              <br/>
            </>
          )}
          {auctionTypeId !== TIMED_AUCTION_TYPE_ID && props.item.sbitem === 1 && (
            <BidSubmittedButton to="/account/bids" title="View All Submitted Bids">
              Bid Submitted - View My Bids
            </BidSubmittedButton>
          )}
        </BidButtons>
        <WishlistAndText>
          <CatalogWishlists
            item={props.item}
            title={auctionTitle}
            inverted
            backgroundColor={'#FFFFFF'}
            noOptionsBorder
          />
          {auctionTypeId !== TIMED_AUCTION_TYPE_ID ? <CatalogTextAlerts item={props.item}/> : null}
        </WishlistAndText>
      </LoginToUse>
    </Actions>
  )
  ;
};
